import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  ArrowLeft,
  Users,
  DollarSign,
  Gamepad2,
  CalendarDays,
  Eye,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Play,
  FolderOpen,
  Globe,
  BarChart2,
} from "lucide-react";

function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [requestSteamKey, setRequestSteamKey] = useState(false);
  const [utmLink, setUtmLink] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  function getYouTubeId(url) {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
    );
    return match ? match[1] : null;
  }

  useEffect(() => {
    async function fetchCampaign() {
      try {
        const snap = await getDoc(doc(db, "campaigns", id));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setCampaign(data);
          if (data.influencers && data.influencers.includes(currentUser?.uid)) {
            setJoined(true);
            // Rebuild UTM link for already-joined state
            const baseUrl = data.steamPageUrl || `https://store.steampowered.com/app/${data.steamAppId}`;
            const utmId = userProfile?.utmId || currentUser?.uid;
            setUtmLink(`${baseUrl}?utm_source=referred.games&utm_medium=influencer&utm_campaign=${snap.id}&utm_content=${utmId}`);
          }
        } else {
          navigate("/campaigns");
        }
      } catch (err) {
        console.error("Failed to fetch campaign:", err);
        navigate("/campaigns");
      }
      setLoading(false);
    }
    fetchCampaign();
  }, [id, currentUser, userProfile?.utmId, navigate]);

  async function handleJoin() {
    setJoining(true);
    setJoinError("");

    try {
      if (
        campaign.maxInfluencers &&
        (campaign.joinedInfluencers || 0) >= campaign.maxInfluencers
      ) {
        setJoinError("This campaign has reached its influencer limit.");
        setJoining(false);
        return;
      }

      if (campaign.status !== "active") {
        setJoinError("This campaign is not currently active.");
        setJoining(false);
        return;
      }

      if (!userProfile?.isApprovedByAdmin) {
        setJoinError("Your account has not been verified by an admin yet.");
        setJoining(false);
        return;
      }

      if (!userProfile?.utmId) {
        setJoinError("Please complete your profile first to get a UTM ID.");
        setJoining(false);
        return;
      }

      const baseUrl = campaign.steamPageUrl || `https://store.steampowered.com/app/${campaign.steamAppId}`;
      const utmId = userProfile.utmId;
      const generatedUtmLink = `${baseUrl}?utm_source=referred.games&utm_medium=influencer&utm_campaign=${id}&utm_content=${utmId}`;

      await updateDoc(doc(db, "campaigns", id), {
        joinedInfluencers: increment(1),
        influencers: arrayUnion(currentUser.uid),
      });

      await setDoc(doc(db, "campaignJoins", `${id}_${currentUser.uid}`), {
        campaignId: id,
        campaignTitle: campaign.title,
        campaignCoverImageUrl: campaign.coverImageUrl || "",
        publisherName: campaign.publisherName || "",
        companyName: campaign.companyName || "",
        campaignType: campaign.campaignType || "wishlist",
        pricePerWishlist: campaign.pricePerWishlist || 0,
        pricePerPurchase: campaign.pricePerPurchase || 0,
        influencerId: currentUser.uid,
        influencerName: currentUser.displayName || "",
        utmId,
        utmLink: generatedUtmLink,
        requestSteamKey,
        joinedAt: serverTimestamp(),
      });

      setCampaign((prev) => ({
        ...prev,
        joinedInfluencers: (prev.joinedInfluencers || 0) + 1,
        influencers: [...(prev.influencers || []), currentUser.uid],
      }));
      setUtmLink(generatedUtmLink);
      setJoined(true);

      try {
        const sendCampaignJoinedEmail = httpsCallable(functions, "sendCampaignJoinedEmail");
        await sendCampaignJoinedEmail({
          campaignId: id,
          campaignTitle: campaign.title,
          gameName: campaign.gameName || "",
          publisherId: campaign.publisherId || "",
          utmLink: generatedUtmLink,
          requestSteamKey,
        });
      } catch (emailErr) {
        console.error("Failed to send campaign joined email:", emailErr);
      }
    } catch (err) {
      console.error("Failed to join campaign:", err);
      setJoinError("Failed to join campaign. Please try again.");
    }
    setJoining(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!campaign) return null;

  const isFull =
    campaign.maxInfluencers &&
    (campaign.joinedInfluencers || 0) >= campaign.maxInfluencers;

  const profileComplete =
    userProfile?.influencerProfile?.profileCompleted === true;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back button */}
        <button
          onClick={() => navigate("/campaigns")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Campaigns
        </button>

        {/* Banner Image */}
        {campaign.bannerImageUrl && (
          <div className="w-full rounded-2xl overflow-hidden mb-8" style={{ aspectRatio: "16/9" }}>
            <img
              src={campaign.bannerImageUrl}
              alt={campaign.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Title & Status */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">{campaign.title}</h1>
            <p className="text-sm text-gray-500">
              by {campaign.companyName || campaign.publisherName}
            </p>
          </div>
          <span
            className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${
              campaign.status === "active" && new Date(campaign.startDate) > new Date()
                ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                : campaign.status === "active"
                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
            }`}
          >
            {campaign.status === "active" && new Date(campaign.startDate) > new Date()
              ? "Starting Soon"
              : campaign.status}
          </span>
        </div>

        {/* Description */}
        <p className="text-gray-400 leading-relaxed mb-4">
          {campaign.description}
        </p>

        {/* Genres */}
        {campaign.genres && campaign.genres.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-8">
            {campaign.genres.map((genre) => (
              <span
                key={genre}
                className="text-xs px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20"
              >
                {genre}
              </span>
            ))}
          </div>
        )}

        {/* Gallery Carousel */}
        {campaign.galleryImageUrls && campaign.galleryImageUrls.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-gray-400 mb-3">
              Screenshots & Media
            </h3>
            <div className="relative rounded-2xl overflow-hidden border border-gray-800">
              <img
                src={campaign.galleryImageUrls[carouselIndex]}
                alt={`Gallery ${carouselIndex + 1}`}
                className="w-full h-64 sm:h-96 object-cover transition-all"
              />
              {campaign.galleryImageUrls.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setCarouselIndex((prev) =>
                        prev === 0 ? campaign.galleryImageUrls.length - 1 : prev - 1
                      )
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCarouselIndex((prev) =>
                        prev === campaign.galleryImageUrls.length - 1 ? 0 : prev + 1
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {campaign.galleryImageUrls.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setCarouselIndex(i)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          i === carouselIndex
                            ? "bg-white scale-125"
                            : "bg-white/40 hover:bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Info Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              {campaign.campaignType === "purchase" ? "Pay per Purchase" : "Pay per Wishlist"}
            </div>
            {campaign.useCountryPricing && campaign.pricingTiers && campaign.pricingTiers.length > 0 ? (
              <div className="space-y-1.5 mt-1">
                {campaign.pricingTiers.map((tier, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-1.5">
                      {tier.countries && tier.countries.length > 0 ? tier.name : (
                        <><Globe className="w-3 h-3" /> {tier.name} (default)</>
                      )}
                    </span>
                    <span className="font-semibold">${tier.price}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xl font-bold">
                ${campaign.campaignType === "purchase" ? campaign.pricePerPurchase : campaign.pricePerWishlist}
              </p>
            )}
          </div>

          {/* Tier Country Breakdown */}
          {campaign.useCountryPricing && campaign.pricingTiers && campaign.pricingTiers.length > 0 && (
            <div className="sm:col-span-2 lg:col-span-3 bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                <Globe className="w-4 h-4" />
                Country Tiers
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {campaign.pricingTiers.map((tier, i) => {
                  const isDefault = !tier.countries || tier.countries.length === 0;
                  return (
                    <div key={i} className="bg-gray-800/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-white">{tier.name}</span>
                        <span className="text-xs font-medium text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded-full">
                          ${tier.price}/{campaign.campaignType === "purchase" ? "purchase" : "wishlist"}
                        </span>
                      </div>
                      {isDefault ? (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          All other countries
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {tier.countries.map((c) => (
                            <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-gray-700/50 text-gray-400">
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <CalendarDays className="w-4 h-4" />
              Campaign Period
            </div>
            <p className="text-sm font-semibold">
              {campaign.startDate} — {campaign.endDate}
            </p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Max Budget
            </div>
            <p className="text-xl font-bold">
              ${campaign.maxBudget?.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Users className="w-4 h-4" />
              Influencer Spots
            </div>
            <p className="text-xl font-bold">
              {campaign.joinedInfluencers || 0}
              <span className="text-base font-normal text-gray-500">
                {" "}
                / {campaign.maxInfluencers || "∞"}
              </span>
            </p>
          </div>
          {campaign.minAudienceSize > 0 && (
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <Eye className="w-4 h-4" />
                Min. Audience
              </div>
              <p className="text-xl font-bold">
                {campaign.minAudienceSize.toLocaleString()}+
              </p>
            </div>
          )}
          {campaign.steamPageUrl && (
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <Gamepad2 className="w-4 h-4" />
                Steam Page
              </div>
              <a
                href={campaign.steamPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-400 hover:text-brand-300 text-sm font-medium flex items-center gap-1.5 transition-colors"
              >
                View on Steam
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </div>

        {/* YouTube Trailer */}
        {getYouTubeId(campaign.youtubeTrailerUrl) && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <Play className="w-4 h-4" /> Game Trailer
            </h3>
            <div className="relative w-full rounded-2xl overflow-hidden border border-gray-800" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(campaign.youtubeTrailerUrl)}`}
                title="Game Trailer"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        )}

        {/* Media Kit */}
        {campaign.mediaKitUrl && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <FolderOpen className="w-4 h-4" /> Media Kit
            </h3>
            <a
              href={campaign.mediaKitUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gray-900/50 border border-gray-800/50 hover:border-brand-500/50 rounded-xl px-5 py-3 text-brand-400 hover:text-brand-300 transition-all text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Open Media Kit
            </a>
          </div>
        )}

        {/* Platforms */}
        {campaign.platforms && campaign.platforms.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-gray-400 mb-3">
              Required Platforms
            </h3>
            <div className="flex flex-wrap gap-2">
              {campaign.platforms.map((p) => (
                <span
                  key={p}
                  className="text-sm px-3 py-1.5 rounded-full bg-gray-900/50 border border-gray-800/50 text-gray-300 capitalize"
                >
                  {p === "twitter" ? "X / Twitter" : p}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Join Button */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
          {!profileComplete ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1">
                  Complete your profile first
                </h3>
                <p className="text-sm text-gray-500">
                  You need to set up your audience size, platforms, links, and payment info before joining campaigns.
                </p>
              </div>
              <button
                onClick={() => navigate("/profile")}
                className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-3 rounded-xl transition-all font-semibold text-sm flex-shrink-0"
              >
                <UserPlus className="w-4 h-4" />
                Set Up Profile
              </button>
            </div>
          ) : joined ? (
            <div>
              <div>
                <p className="font-semibold text-green-400 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  You've joined this campaign!
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Use the UTM link to promote this game.
                  {campaign.steamAccessKey && (
                    <span className="block mt-1">
                      Game Key:{" "}
                      <code className="bg-gray-800 px-2 py-0.5 rounded text-gray-300">
                        {campaign.steamAccessKey}
                      </code>
                    </span>
                  )}
                </p>
                {utmLink && (
                  <div className="mt-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                    <p className="text-xs text-gray-500 mb-1">Your UTM Link</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-brand-400 break-all flex-1">
                        {utmLink}
                      </code>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(utmLink)}
                        className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {/* Important notices */}
                <div className="mt-4 space-y-2">
                  {/* <div className="flex gap-2.5 bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3">
                    <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-300/80 leading-relaxed">
                      <span className="font-semibold text-yellow-300">Data verification and processing can take up to 2 weeks.</span> Please allow time for your wishlists and purchases to be verified and reflected in your earnings.
                    </p>
                  </div> */}
                  <div className="flex gap-2.5 bg-orange-500/5 border border-orange-500/20 rounded-xl p-3">
                    <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-orange-300/80 leading-relaxed">
                      <span className="font-semibold text-orange-300">You must submit every post or video where you used the UTM link.</span> Go to your Dashboard, click on this campaign, and scroll down to add your posts. This is crucial for us to verify the wishlists and purchases that came from your link.
                    </p>
                  </div>
                  {/* <div className="flex gap-2.5 bg-red-500/5 border border-red-500/20 rounded-xl p-3">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-300/80 leading-relaxed">
                      <span className="font-semibold text-red-300">All posts will be reviewed for legitimacy.</span> We will verify that your submitted posts are genuine, and only referrals from legitimate posts will be accepted and counted toward your earnings.
                    </p>
                  </div> */}
                </div>

                <button
                  onClick={() => navigate(`/campaigns/${id}/performance`)}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-5 py-3 rounded-xl transition-all font-semibold text-sm"
                >
                  <BarChart2 className="w-4 h-4" />
                  View My Performance / Add Posts
                </button>
              </div>
            </div>
          ) : !userProfile?.isApprovedByAdmin ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1 text-yellow-300">
                  Account Not Verified
                </h3>
                <p className="text-sm text-gray-500">
                  Your account needs to be verified by an admin before you can join campaigns. Please wait for approval.
                </p>
              </div>
              <span className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 px-4 py-2.5 rounded-xl text-sm font-medium flex-shrink-0">
                <AlertCircle className="w-4 h-4" />
                Pending Verification
              </span>
            </div>
          ) : !currentUser?.emailVerified ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1 text-yellow-300">
                  Verify your email first
                </h3>
                <p className="text-sm text-gray-500">
                  You need to verify your email address before you can join campaigns. Go to your profile to send a verification email.
                </p>
              </div>
              <button
                onClick={() => navigate("/profile")}
                className="flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-6 py-3 rounded-xl transition-all font-semibold text-sm flex-shrink-0"
              >
                <AlertCircle className="w-4 h-4" />
                Verify Email
              </button>
            </div>
          ) : (
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg mb-1">
                    Ready to promote this game?
                  </h3>
                  <p className="text-sm text-gray-500">
                    {isFull
                      ? "This campaign has reached its influencer limit."
                      : campaign.status !== "active"
                      ? "This campaign is currently paused."
                      : campaign.campaignType === "purchase"
                      ? `Earn $${campaign.pricePerPurchase} for every purchase you generate.`
                      : `Earn $${campaign.pricePerWishlist} for every wishlist you generate.`}
                  </p>
                </div>
                <button
                  onClick={() => setShowDisclaimer(true)}
                  disabled={joining || isFull || campaign.status !== "active"}
                  className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl transition-all font-semibold text-sm flex-shrink-0"
                >
                  {joining ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  Join Campaign
                </button>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={requestSteamKey}
                  onChange={(e) => setRequestSteamKey(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-700 bg-gray-900/50 text-brand-500 focus:ring-brand-500/50"
                />
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                  Request a Steam key for this game
                </span>
              </label>
            </div>
          )}
          {joinError && (
            <div className="flex items-center gap-2 mt-3 text-sm text-red-400">
              <AlertCircle className="w-4 h-4" />
              {joinError}
            </div>
          )}
        </div>

        {/* Disclaimer Modal */}
        {showDisclaimer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-brand-500/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-brand-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Campaign Guidelines</h3>
                  <p className="text-xs text-gray-500">Please read carefully before joining</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                  <span className="text-brand-400 font-bold text-sm mt-0.5">1.</span>
                  <p className="text-sm text-gray-300">I will <span className="font-semibold text-white">not use bots, fake accounts, or any automated tools</span> to artificially inflate wishlists, purchases, or any other campaign metrics.</p>
                </div>
                <div className="flex gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                  <span className="text-brand-400 font-bold text-sm mt-0.5">2.</span>
                  <p className="text-sm text-gray-300">I will <span className="font-semibold text-white">only promote this game through legitimate content</span> on my social media channels, streams, or videos.</p>
                </div>
                <div className="flex gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                  <span className="text-brand-400 font-bold text-sm mt-0.5">3.</span>
                  <p className="text-sm text-gray-300">I will <span className="font-semibold text-white">submit every post or video</span> where I used the UTM link so it can be verified.</p>
                </div>
                <div className="flex gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                  <span className="text-brand-400 font-bold text-sm mt-0.5">4.</span>
                  <p className="text-sm text-gray-300">I understand that <span className="font-semibold text-white">all referrals will be reviewed for legitimacy</span> and only genuine referrals will count toward my earnings.</p>
                </div>
                <div className="flex gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                  <span className="text-brand-400 font-bold text-sm mt-0.5">5.</span>
                  <p className="text-sm text-gray-300">I understand that <span className="font-semibold text-white">data verification and processing can take up to 2 weeks</span> before results are reflected in my earnings.</p>
                </div>
                <div className="flex gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                  <span className="text-brand-400 font-bold text-sm mt-0.5">6.</span>
                  <p className="text-sm text-gray-300">I understand that <span className="font-semibold text-white">violating any of these guidelines may result in removal</span> from the campaign and forfeiture of earnings.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDisclaimer(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 py-3 rounded-xl transition-all font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowDisclaimer(false);
                    handleJoin();
                  }}
                  disabled={joining}
                  className="flex-1 bg-brand-600 hover:bg-brand-500 text-white py-3 rounded-xl transition-all font-semibold text-sm disabled:opacity-50"
                >
                  I Read & Understand — Join Campaign
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignDetailPage;
